import React, { useId, useState } from 'react';

export function permissionObject(raw) {
  const value = typeof raw === 'string' ? JSON.parse(raw) : (raw ?? {});
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Permissions must be a JSON object');
  }
  return value;
}

export function togglePermissionValue(previous, module, path, type, selectAllAllowed = false) {
  const row = previous[module]?.[path] || {};
  const checked = !row[type];
  const changes = selectAllAllowed && type === 'allowed'
    ? { allowed: checked, add: checked, edit: checked, view: checked, print: checked }
    : { [type]: checked };
  return { ...previous, [module]: { ...previous[module], [path]: { ...row, ...changes } } };
}

// Both editors receive the existing User Rights catalogue unchanged.
export default function PermissionMatrix({ config, permissions, setPermissions, branches = [], selectAllAllowed = false, showAllModules = false }) {
  const [selectedModule, setSelectedModule] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const matrixId = useId();
  const modules = showAllModules ? config : config.filter(item => item.key === selectedModule);
  const selectedCount = module => {
    if (module.key === 'admin') {
      return branches.filter(branch => (permissions.admin?.branches || [])
        .some(id => String(id) === String(branch.id))).length;
    }
    const actions = module.key === 'report' ? ['access', 'print'] : ['allowed', 'add', 'edit', 'view', 'print'];
    return module.menus.reduce((count, menu) => count + actions.filter(action =>
      Boolean(permissions[module.key]?.[menu.path]?.[action])).length, 0);
  };
  const toggle = (moduleKey, path, type) => setPermissions(previous =>
    togglePermissionValue(previous, moduleKey, path, type, selectAllAllowed));
  const toggleBranch = id => setPermissions(previous => {
    const current = previous.admin?.branches || [];
    const selected = current.some(value => String(value) === String(id));
    return { ...previous, admin: { ...previous.admin, branches: selected
      ? current.filter(value => String(value) !== String(id)) : [...current, id] } };
  });
  return <div className="col-12 mt-3">
    {!showAllModules && <>
    <label className="form-label">Module</label>
    <select className="form-control" value={selectedModule} onChange={event => setSelectedModule(event.target.value)}>
      <option value="">Select Module</option>
      {config.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
    </select>
    </>}
    {showAllModules && <div className="d-flex justify-content-end gap-2 mb-2">
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() =>
        setExpandedModules(Object.fromEntries(config.map(module => [module.key, true])))}>Expand All</button>
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setExpandedModules({})}>Collapse All</button>
    </div>}
    {modules.map((module, index) => <div key={module.key} className={showAllModules
      ? 'permissions-card mx-auto mt-2 shadow-sm border rounded bg-white overflow-hidden'
      : 'permissions-card mx-auto mt-4 p-3 shadow rounded bg-white'}>
      {showAllModules ? <h5 className="mb-0">
        <button type="button" className="btn btn-light w-100 rounded-0 d-flex align-items-center justify-content-between text-start p-3 fw-semibold"
          aria-expanded={Boolean(expandedModules[module.key])} aria-controls={`${matrixId}-module-${index}`}
          onClick={() => setExpandedModules(previous => ({ ...previous, [module.key]: !previous[module.key] }))}>
          <span>{module.label} — {selectedCount(module)} selected</span>
          <span aria-hidden="true">{expandedModules[module.key] ? '−' : '+'}</span>
        </button>
      </h5> : <h5 className="text-center mb-3">{module.label} Permissions</h5>}
      <div id={`${matrixId}-module-${index}`} hidden={showAllModules && !expandedModules[module.key]}
        className={showAllModules ? 'p-3 border-top' : undefined}>
      <div className="table-responsive"><table className="table table-bordered table-striped table-hover">
        <thead className="table-info"><tr>
          <th>{module.key === 'admin' ? 'Branch Name' : 'Module'}</th>
          {(module.key === 'admin' ? ['Allowed'] : module.key === 'report'
            ? ['Access', 'Print'] : ['Allowed', 'Add', 'Edit', 'View', 'Print'])
            .map(label => <th key={label} className="text-center">{label}</th>)}
        </tr></thead>
        <tbody>{module.key === 'admin' ? (branches.length ? branches.map(branch =>
          <tr key={branch.id}><td>{branch.name}</td><td className="text-center">
            <input type="checkbox" aria-label={`${branch.name} Allowed`}
              checked={(permissions.admin?.branches || []).some(id => String(id) === String(branch.id))}
              onChange={() => toggleBranch(branch.id)} />
          </td></tr>) : <tr><td colSpan="2">No branches found</td></tr>)
          : module.menus.map(menu => {
            const row = permissions[module.key]?.[menu.path] || {};
            const actions = module.key === 'report' ? ['access', 'print'] : ['allowed', 'add', 'edit', 'view', 'print'];
            return <tr key={menu.path}><td>{menu.label}</td>{actions.map(action =>
              <td key={action} className="text-center"><input type="checkbox"
                aria-label={`${menu.label} ${action}`} checked={Boolean(row[action])}
                disabled={module.key === 'report' ? action === 'print' && !row.access : action !== 'allowed' && !row.allowed}
                onChange={() => toggle(module.key, menu.path, action)} /></td>)}</tr>;
          })}</tbody>
      </table></div>
      </div>
      {!showAllModules && <button type="button" className="btn btn-secondary" onClick={() => setSelectedModule('')}>Cancel</button>}
    </div>)}
  </div>;
}
